<template>
  <wui-modal
    :size="40"
    :closable="true"
    class="h-160! max-h-[calc(100vh-32px)]! w-[min(32.5rem,calc(100vw-32px))]! px-4! pt-4! pb-5! [&_.wui-modal__heading]:py-0"
  >
    <template #header>
      <h2 class="text-heading-sb-18-24 text-fg-base-fg3">
        {{ $t('chat.addToChat') }}
      </h2>
    </template>

    <div class="flex h-full min-h-0 flex-col">
      <div class="shrink-0 pb-2">
        <wui-input
          v-model="search"
          :placeholder="$t('common.search')"
          with-clear
          prepend-icon="searchM"
        />
      </div>

      <div class="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
        <template
          v-for="(room, index) in displayRooms"
          :key="room.pServedUser"
        >
          <hr
            v-if="index === dividerBeforeIndex"
            class="my-1 border-t-bor-nav"
            data-test="add-contact-to-chats-pinned-divider"
          >
          <button
            type="button"
            class="flex w-full items-center gap-3 rounded-8 py-1.5 text-left"
            :data-test="`add-contact-to-chats-room-${room.pServedUser}`"
            @click="toggleRoom(room.pServedUser)"
          >
            <contact-avatar
              :room="room"
              :is-room="true"
              :size="40"
              class="shrink-0"
            />
            <span
              class="min-w-0 grow truncate text-body-m-15-22 text-fg-base-fg3"
              :title="room.name"
            >
              {{ room.name }}
            </span>
            <wui-checkbox
              :model-value="selectedPServeds.includes(room.pServedUser)"
              class="pointer-events-none shrink-0"
              small
              readonly
            />
          </button>
        </template>

        <p
          v-if="!displayRooms.length"
          class="px-3 py-2 text-fg-base-fg10"
        >
          {{ $t('chat.noChatsFound') }}
        </p>
      </div>
    </div>

    <template #footer>
      <wui-btn
        :size="40"
        variant="neut"
        state="alpha"
        data-test="add-contact-to-chats-cancel"
        @click="closeDialog"
      >
        {{ $t('common.cancel') }}
      </wui-btn>
      <wui-btn
        :size="40"
        variant="brand"
        state="filled"
        :disabled="!selectedPServeds.length"
        auto-loading
        data-test="add-contact-to-chats-submit"
        @click="addToSelectedChats"
      >
        {{ $t('common.add') }}
      </wui-btn>
    </template>
  </wui-modal>
</template>

<script setup lang="ts">
import { closeDialogKey, safeInject, useRenderNotifier, WuiBtn, WuiCheckbox, WuiInput, WuiModal } from '@wui/common-library'
import { isBotPServed, type PServed } from '@wui/im'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { ContactAvatar } from '@/entities/contact-avatar'
import { useRoom } from '@/entities/use-room'

import { useLocalization } from '@/shared/composables/localization'
import type { Contact } from '@/shared/types/api'

import { useEligibleGroupRoomsForContact } from '../lib/use-eligible-group-rooms'

const props = defineProps<{
  contact: Contact
}>()

const closeModal = safeInject(closeDialogKey)
const { t, tc } = useLocalization()
const { notifyError } = useRenderNotifier()
const { addMember } = useRoom()
const route = useRoute()
const { eligibleRooms } = useEligibleGroupRoomsForContact(() => props.contact)

const search = ref('')
const selectedPServeds = ref<PServed[]>([])

const matchingRooms = computed(() => {
  const query = search.value.trim().toLowerCase()
  if (!query) {
    return eligibleRooms.value
  }

  return eligibleRooms.value.filter((room) => room.name.toLowerCase().includes(query))
})

const pinnedRooms = computed(() => matchingRooms.value.filter((room) => room.isPinned))
const unpinnedRooms = computed(() => matchingRooms.value.filter((room) => !room.isPinned))
const displayRooms = computed(() => [...pinnedRooms.value, ...unpinnedRooms.value])
const dividerBeforeIndex = computed(() => {
  if (!pinnedRooms.value.length || !unpinnedRooms.value.length) {
    return -1
  }

  return pinnedRooms.value.length
})

const getAddMemberTarget = (contact: Contact) => {
  if (contact.imLogin && isBotPServed(contact.imLogin)) {
    return contact.imLogin
  }

  return contact.internalNumber || contact.imLogin
}

const toggleRoom = (pServedUser: PServed) => {
  if (selectedPServeds.value.includes(pServedUser)) {
    selectedPServeds.value = selectedPServeds.value.filter((item) => item !== pServedUser)
    return
  }

  selectedPServeds.value = [...selectedPServeds.value, pServedUser]
}

const closeDialog = () => {
  closeModal(undefined)
}

const addToSelectedChats = async () => {
  const target = getAddMemberTarget(props.contact)
  if (!target || !selectedPServeds.value.length) {
    notifyError(t('common.failedToAddSubscriber'))
    return
  }

  const results = await Promise.allSettled(
    selectedPServeds.value.map((pServed) => addMember(pServed, target, 'AddMember')),
  )
  const failedCount = results.filter((result) => result.status === 'rejected').length

  if (failedCount === results.length) {
    notifyError(t('common.failedToAddSubscriber'))
    return
  }

  if (failedCount > 0) {
    notifyError(tc('common.errorOccuredAddingMembers', failedCount, { n: failedCount }))
  }

  closeDialog()
}

watch(() => route.path, () => {
  closeDialog()
})
</script>
